import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiConteudoSlice } from 'services/apiConteudo';
import { backendApiSlice, useGetProfilesQuery } from 'services/backend';
import { cmsApiSlice } from 'services/cms';
import { mediaApiSlice } from 'services/media';
import { logoutAction } from 'store/actions';
import { RootState, persistor } from 'store/index';
import { clearServerSession } from 'utils/clearServerSession';
import { Platform } from 'utils/platform';
import { revokeAppAccess } from 'utils/revokeAppAccess';

import { AccountNotFoundModal } from 'components/AccountDeactivated/AccountNotFoundModal';
import { usePlayer } from 'components/Player/context';

const MODAL_DELAY_MS = 400;

let hasPendingNotFoundModal = false;

export const AccountDeactivatedGuard: React.FC = () => {
  const dispatch = useDispatch();
  const { pause, reset } = usePlayer();
  const { isAuthenticated, userToken } = useSelector((state: RootState) => state.auth);
  const selectedProfileId = useSelector((state: RootState) => state.profile.selectedProfileId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isBlockingRef = useRef(false);
  const modalTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const { isAccountDeactivated } = useGetProfilesQuery(undefined, {
    skip: !isAuthenticated,
    selectFromResult: ({ data }) => ({
      isAccountDeactivated: !!data?.some((profile) => profile.wasAccountDeactivated),
    }),
  });

  const scheduleNotFoundModal = () => {
    clearTimeout(modalTimerRef.current);
    modalTimerRef.current = setTimeout(() => {
      hasPendingNotFoundModal = false;
      setIsModalOpen(true);
    }, MODAL_DELAY_MS);
  };

  const blockAccount = () => {
    const shouldShowModal = !Platform.isTV || !selectedProfileId;

    if (userToken) {
      revokeAppAccess(userToken).catch(console.error);
    }

    pause();
    reset({ media: undefined });

    if (Platform.isTV) {
      document.cookie = 'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    } else {
      clearServerSession().catch(console.error);
    }

    dispatch(logoutAction());
    dispatch(backendApiSlice.util.resetApiState());
    dispatch(cmsApiSlice.util.resetApiState());
    dispatch(apiConteudoSlice.util.resetApiState());
    dispatch(mediaApiSlice.util.resetApiState());
    persistor.flush();

    if (shouldShowModal) {
      hasPendingNotFoundModal = true;
      scheduleNotFoundModal();
    }
  };

  const handleCloseModal = useCallback(() => setIsModalOpen(false), []);

  useEffect(() => {
    if (!isAuthenticated) {
      isBlockingRef.current = false;
      return;
    }

    if (!isAccountDeactivated || isBlockingRef.current) return;

    isBlockingRef.current = true;
    blockAccount();
  }, [isAuthenticated, isAccountDeactivated]);

  useEffect(() => {
    if (hasPendingNotFoundModal) scheduleNotFoundModal();

    return () => clearTimeout(modalTimerRef.current);
  }, []);

  return <AccountNotFoundModal isOpen={isModalOpen} onClose={handleCloseModal} />;
};
